import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { BigNumber, Button, FormattedNumber } from '../../../../components'
import { BoxChart } from '../../../../components/Charts/BoxChart'
import { CircularProgress } from '../../../../components/CircularProgress'
import { endOfMonth, startOfMonth, subMonths } from 'date-fns'
import { GetMonthTasksUsageWithPredictionQuery } from '@indigohive/cogfy-types/endpoints/getMonthTasksUsageWithPrediction'
import { MixedChart } from '../../../../components/Charts/MixedChart'
import { useCogfy } from '../../../../hooks/use-cogfy'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTasksUsageSum } from '../../../../hooks/use-tasks-usage-sum'
import { useTranslation } from 'react-i18next'

export function TasksSection () {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const cogfy = useCogfy()
  const params = useParams<{ workspace: string }>()
  const workspace = params.workspace!

  const query: GetMonthTasksUsageWithPredictionQuery = {
    startOfMonth: startOfMonth(new Date()),
    endOfMonth: endOfMonth(new Date()),
    timezoneOffset: new Date().getTimezoneOffset() / -60
  }
  const {
    data: monthlyTasksUsagePrediction,
    isLoading: isLoadingMonthlyTasksUsagePrediction,
    isError: isErrorMonthlyTasksUsagePrediction,
    refetch: refetchMonthlyTasksUsagePrediction
  } = useQuery({
    queryKey: ['getMonthTasksUsageWithPrediction', query],
    queryFn: ({ signal }) => cogfy.getMonthTasksUsageWithPrediction(query, { signal })
  })

  const tasksPrediction = monthlyTasksUsagePrediction?.data
  const tasksQuota = monthlyTasksUsagePrediction?.planTasksQuota
  const tasksUsageForecast = tasksPrediction
    ? tasksPrediction[tasksPrediction?.length - 1]?.predictedTasksAccumulatedUsage || 0
    : undefined
  const consumedTasks = tasksPrediction && Math.max(...tasksPrediction.map(item => item.tasksAccumulatedUsage)) > 0
    ? Math.max(...tasksPrediction.map(item => item.tasksAccumulatedUsage))
    : 0

  const {
    data: tasksUsageFromLastMonth,
    isError: errorTasksUsageFromLastMonth,
    isLoading: loadingTasksUsageFromLastMonth,
    refetch: refetchTasksUsageFromLastMonth
  } = useTasksUsageSum(workspace, {
    startDate: startOfMonth(subMonths(new Date(), 1)),
    endDate: endOfMonth(subMonths(new Date(), 1))
  })

  const availableTasks = tasksQuota && tasksQuota > 0
    ? tasksQuota
    : 0

  const consumedPercentage = getPercentage(availableTasks, consumedTasks)

  const tasksUsageGrowthPercentage = tasksUsageFromLastMonth
    ? 100 * ((tasksUsageForecast ?? 0) - tasksUsageFromLastMonth) / tasksUsageFromLastMonth
    : 0

  const usageData = {
    labels: tasksPrediction?.flatMap(item => item.dateLabel),
    datasets: [
      {
        type: 'bar' as const,
        label: t('workspaceUsagePage:Consumed'),
        data: tasksPrediction?.flatMap(item => item.tasksAccumulatedUsage) || [],
        backgroundColor: ['#08BDAB']
      },
      {
        type: 'bar' as const,
        label: t('workspaceUsagePage:Predicted'),
        data: tasksPrediction?.flatMap(item => Math.ceil(item.predictedTasksAccumulatedUsage)) || [],
        backgroundColor: ['#F75A11']
      },
      {
        type: 'line' as const,
        label: 'Quota',
        borderColor: '#808080',
        backgroundColor: '#808080',
        borderWidth: 2,
        fill: false,
        data: Array(tasksPrediction?.length).fill(tasksQuota),
        spanGaps: true,
        borderDash: [2, 2],
        radius: 0
      }
    ]
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex w-full justify-between'>
        <h2 className="text-2xl font-semibold">Tasks</h2>
        <Button
          className="w-fit bg-transparent h-auto px-3 border-primary text-primary hover:bg-primary hover:text-white"
          onClick={() => navigate('../tasks')}
        >
          {t('Details')}
        </Button>
      </div>
      <div className="flex flex-row gap-4 items-stretch">
        <BigNumber.Card
          className="basis-2/4"
          loading={loadingTasksUsageFromLastMonth}
          error={errorTasksUsageFromLastMonth}
          empty={tasksUsageFromLastMonth === undefined}
          onRefetchClick={() => {
            refetchTasksUsageFromLastMonth()
            refetchMonthlyTasksUsagePrediction()
          }}
        >
          <span>{t('workspaceUsagePage:You used this month')}</span>
          <BigNumber.Number>
            <CircularProgress
              size={148}
              percentageTextClassName="font-bold text-3xl text-black"
              percentage={consumedPercentage}
              renderPercentage={true}
            />
          </BigNumber.Number>
          <BigNumber.AdditionalInfo>
            {t('workspaceUsagePage:Last month') + ': '}
            <FormattedNumber value={tasksUsageFromLastMonth || 0} className="font-bold" />
          </BigNumber.AdditionalInfo>
        </BigNumber.Card>

        <BoxChart
          aside={
            <div>
              {t('workspaceUsagePage:Forecast for this month')}
              <div className='flex flex-row items-end'>
                <div className='font-bold text-3xl'>
                  <FormattedNumber className='inline-flex' value={tasksUsageForecast ? Math.ceil(tasksUsageForecast) : 0}/>
                </div>
                <span
                  className={'tooltip tooltip-top pl-1 before:w-[200px] before:content-[attr(data-tip)]'}
                  data-tip='Em relação ao mês anterior'
                >
                  <div className='flex flex-row mb-0.75'>
                    {tasksUsageGrowthPercentage < 0 && <ArrowDownRight size={20} className='text-gray-400' />}
                    {tasksUsageGrowthPercentage > 0 && <ArrowUpRight size={20} className='text-gray-400' />}
                    {tasksUsageGrowthPercentage !== 0 && (
                      <>
                        <FormattedNumber className='text-sm text-light text-gray-400' value={Math.abs(tasksUsageGrowthPercentage)} compact/>
                        <span className='text-sm text-light text-gray-400'>%</span>
                      </>
                    )}
                  </div>
                </span>
              </div>
              <span className='text-sm text-light text-gray-400'></span>
            </div>
          }
          asideClassName="items-center justify-center"
        >
          <MixedChart
            data={usageData || []}
            legend={{ position: 'top' }}
            stacked={true}
            loading={isLoadingMonthlyTasksUsagePrediction}
            error={isErrorMonthlyTasksUsagePrediction}
          />
        </BoxChart>
      </div>
    </div>
  )
}

const getPercentage = (available: number, consumed: number): number => {
  if (available === 0 && consumed === 0) {
    return 0
  } else if (available === 0 && consumed > 0) {
    return 100
  } else {
    return Math.floor((consumed / available) * 100)
  }
}
